import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  UserIcon,
  DocumentTextIcon,
  ClockIcon,
  ListBulletIcon,
  PlayIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import LogoSvg from "../../common/assets/images/logo1.svg";
import { ChevronRight, TrophyIcon } from "lucide-react";
import { getExamById } from "../../services/ExamService";
import { localStorageGetItem } from "../../common/localStorage";
import { toast } from "react-toastify";
import RightList from "./RightList";
import { Link } from "react-router-dom";

const ExamInfoPage = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [examData, setExamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);

  // Fetch exam data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getExamById(examId);

        if (response?.data) {
          setExamData(response.data);
        } else {
          toast.error("Không tìm thấy đề thi");
          navigate("/practice-room");
        }
      } catch (error) {
        console.error("Error fetching exam data:", error);
        toast.error("Cần nâng cấp tài khoản để xem đề thi");
        navigate("/practice-room");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Get user info from localStorage
    const user = localStorageGetItem("user");
    if (user) {
      setUserInfo(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  const handleStartExam = () => {
    navigate(`/exam-test/${examId}`);
  };

  const handleDownloadPDF = () => {
    // TODO: Download PDF
    console.log("Download PDF:", examId);
    // alert("Tải file PDF!");
  };

  const handlePrev = () => {
    // TODO: Navigate to previous exam
    navigate(-1);
  };

  const handleNext = () => {
    // TODO: Navigate to next exam
    // navigate(1);
  };

  const handleRanking = () => {
    navigate(`/exam-result/${examId}`);
  };

  const handleRetakeExam = () => {
    navigate(`/exam-test/${examId}`);
  };

  const getDownloadUrl = (examData) => {
    if (!examData?.url) return;
    const match = examData?.url?.match(/\/d\/(.*?)\//);
    const fileId = match?.[1];
    return fileId
      ? `https://drive.google.com/uc?export=download&id=${fileId}`
      : null;
  };
  return (
    <div className="min-h-screen bg-[#f9dfe8] pb-16">
      {/* Section title bar */}
      <div className="max-w-[100rem] mx-auto px-2 sm:px-4 flex items-center flex-wrap gap-1 sm:gap-2">
        <div className="mt-4 inline-block bg-[#db3b6d] text-white px-3 sm:px-6 py-2 sm:py-3 rounded-r-md rounded-l-none font-bold shadow text-sm sm:text-base ml-[-100vw] pl-[100vw]">
          PHÒNG LUYỆN ĐỀ
        </div>
        <Link to="/practice-room">
          <div className="flex items-center gap-1 sm:gap-2 mt-4">
            <ChevronRight className="text-[#4b4b4b] ml-[5px] w-5 h-5 sm:w-8 sm:h-8" />
            <span className="text-[#4b4b4b] text-sm sm:text-lg">
              ĐỀ THI TSA
            </span>
          </div>
        </Link>
        <div className="flex items-center gap-1 sm:gap-2 mt-4">
          <ChevronRight className="text-[#4b4b4b] ml-[5px] w-5 h-5 sm:w-8 sm:h-8" />
          {loading ? (
            <div className="h-5 sm:h-6 w-24 sm:w-32 bg-gray-300 rounded animate-pulse"></div>
          ) : (
            <span className="text-[#4b4b4b] text-sm sm:text-lg truncate max-w-[150px] sm:max-w-none">
              {examData?.title || ""}
            </span>
          )}
        </div>
      </div>

      {/* Main Content */}
      {loading ? (
        <section className="max-w-[88rem] mx-auto px-4 mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_320px] gap-5 xl:gap-[10rem]">
            {/* Central Content - Exam Info Card Skeleton */}
            <div className="flex justify-center lg:justify-end">
              <div className="bg-[#f6d2df] rounded-md p-6 shadow-md w-full animate-pulse max-w-[520px]">
                {/* Logo and Title Section */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-gray-300 rounded-full flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="h-6 bg-gray-300 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-32"></div>
                  </div>
                </div>

                {/* Section Header */}
                <div className="h-6 bg-gray-300 rounded w-64 mx-auto mb-4"></div>

                {/* Test Details */}
                <div className="space-y-3 mb-6 px-[67px]">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-300 rounded-full flex-shrink-0"></div>
                    <div className="h-4 bg-gray-300 rounded w-40"></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-300 rounded-full flex-shrink-0"></div>
                    <div className="h-4 bg-gray-300 rounded w-48"></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-300 rounded-full flex-shrink-0"></div>
                    <div className="h-4 bg-gray-300 rounded w-36"></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gray-300 rounded-full flex-shrink-0"></div>
                    <div className="h-4 bg-gray-300 rounded w-44"></div>
                  </div>
                </div>

                {/* Note */}
                <div className="bg-gray-200 rounded-md p-3 mb-5 h-16"></div>

                {/* Start Button */}
                <div className="w-full h-12 bg-gray-300 rounded-md mb-5"></div>

                {/* Download PDF Section */}
                <div className="flex items-center justify-between">
                  <div className="h-10 w-32 bg-gray-300 rounded-md"></div>
                  <div className="h-3 bg-gray-300 rounded w-40"></div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gray-300 rounded-md"></div>
                    <div className="w-10 h-10 bg-gray-300 rounded-md"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Latest Tests Skeleton */}
            {/* <div className="hidden lg:flex items-start">
              <div className="bg-[#f1c0cf] rounded-md p-8 w-full max-w-[320px] shadow-[0_8px_20px_rgba(219,59,109,0.15)] animate-pulse">
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div className="space-y-3">
                  {[...Array(7)].map((_, n) => (
                    <div key={n} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-gray-300"></div>
                      <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div> */}
          </div>

          {/* Right Sidebar on mobile (below main content) */}
          {/* <div className="mt-6 flex justify-center lg:hidden">
            <div className="bg-[#f1c0cf] rounded-md p-8 w-full max-w-[320px] shadow-[0_8px_20px_rgba(219,59,109,0.15)] animate-pulse">
              <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="space-y-3">
                {[...Array(7)].map((_, n) => (
                  <div key={n} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-gray-300"></div>
                    <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            </div>
          </div> */}
        </section>
      ) : examData ? (
        <section className="max-w-[88rem] mx-auto px-4 mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_320px] gap-5 xl:gap-[10rem]">
            {/* Central Content - Exam Info Card */}
            <div className="flex justify-center lg:justify-end">
              <div
                className={`bg-[#f6d2df] rounded-md p-4 sm:p-6 shadow-md w-full ${
                  examData?.isDone ? "max-w-[550px]" : "max-w-[520px]"
                }`}
              >
                {/* Logo and Title Section */}
                <div className="flex items-start gap-2 sm:gap-4 mb-4 sm:mb-6">
                  <img
                    src={LogoSvg}
                    alt="PIMAX"
                    className="w-10 h-10 sm:w-14 sm:h-14 flex-shrink-0"
                  />
                  <div>
                    <h1 className="text-[#db3b6d] font-extrabold text-base sm:text-xl md:text-2xl mb-1 leading-tight">
                      BÀI THI THỬ ĐÁNH GIÁ TƯ DUY
                    </h1>
                    <p className="text-[#4b4b4b] text-xs sm:text-sm mb-0">
                      MÔ PHỎNG THEO KỲ THI TSA
                    </p>
                  </div>
                </div>

                {/* Section Header */}
                <h2 className="text-[#4b4b4b] font-bold text-sm sm:text-base md:text-lg mb-3 sm:mb-4 text-center">
                  XÁC NHẬN THÔNG TIN DỰ THI
                </h2>

                {/* Test Details */}
                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 px-2 sm:px-4 md:px-[67px]">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#db3b6d] flex-shrink-0" />
                    <div className="text-[#4b4b4b] text-xs sm:text-sm break-words">
                      <span className="font-bold">HỌ VÀ TÊN:</span>{" "}
                      {userInfo?.fullName?.toUpperCase() ||
                        userInfo?.identifier?.toUpperCase() ||
                        "N/A"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <DocumentTextIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#db3b6d] flex-shrink-0" />
                    <div className="text-[#4b4b4b] text-xs sm:text-sm break-words">
                      <span className="font-bold">KÍP THI:</span>{" "}
                      {examData.title}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#db3b6d] flex-shrink-0" />
                    <div className="text-[#4b4b4b] text-xs sm:text-sm">
                      <span className="font-bold">THỜI GIAN LÀM BÀI:</span>{" "}
                      {examData.time} PHÚT
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <ListBulletIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#db3b6d] flex-shrink-0" />
                    <div className="text-[#4b4b4b] text-xs sm:text-sm">
                      <span className="font-bold">SỐ LƯỢNG CÂU HỎI:</span>{" "}
                      {examData.numberOfQuestions} CÂU
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div className="bg-white rounded-md p-2 sm:p-3 mb-4 sm:mb-5 border-l-4 border-[#db3b6d]">
                  <p className="text-[#4b4b4b] text-xs italic leading-relaxed">
                    <span className="font-semibold">Lưu ý:</span>{" "}
                    {examData?.isDone
                      ? `Bằng việc nhấn nút "Thi lại" hệ thống sẽ bắt đầu tính giờ cho bài thi của bạn`
                      : `Bằng việc nhấn nút "Bắt đầu thi" hệ thống sẽ bắt đầu tính giờ cho bài thi của bạn`}
                  </p>
                </div>

                {/* Action Buttons - Ranking and Retake */}
                {examData.isDone ? (
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-5">
                    <button
                      onClick={handleRanking}
                      className="flex-1 bg-[#db3b6d] text-white font-bold text-sm sm:text-base py-2.5 sm:py-3 rounded-md flex items-center justify-center gap-2 hover:bg-[#a92e55] transition-colors shadow-lg"
                    >
                      <TrophyIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="whitespace-nowrap">BẢNG XẾP HẠNG</span>
                    </button>
                    <button
                      onClick={handleRetakeExam}
                      className="flex-1 bg-[#db3b6d] text-white font-bold text-sm sm:text-base py-2.5 sm:py-3 rounded-md flex items-center justify-center gap-2 hover:bg-[#a92e55] transition-colors shadow-lg"
                    >
                      <ArrowPathIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="whitespace-nowrap">THI LẠI</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleStartExam}
                    className="w-full bg-[#db3b6d] text-white font-bold text-sm sm:text-base py-2.5 sm:py-3 rounded-md mb-4 sm:mb-5 flex items-center justify-center gap-2 hover:bg-[#a92e55] transition-colors shadow-lg"
                  >
                    <PlayIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    BẮT ĐẦU THI
                  </button>
                )}

                {/* Start Button */}

                {/* Download PDF Section */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2">
                  {examData?.isDone && (
                    <>
                      <Link
                        to={getDownloadUrl(examData)}
                        target="_blank"
                        className="w-full sm:w-auto"
                      >
                        <button
                          onClick={handleDownloadPDF}
                          className="w-full sm:w-auto bg-[#f6d2df] text-[#4b4b4b] hover:bg-[#db3b6d] hover:text-white hover:border-[#db3b6d] font-medium px-4 sm:px-5 py-2 rounded-md flex items-center justify-center gap-2 transition-colors border border-[#4b4b4b] text-xs sm:text-sm"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                          Tải file PDF
                        </button>
                      </Link>

                      <p className="text-[#4b4b4b] text-xs text-center sm:text-left sm:mr-[4.5rem] flex-1 sm:flex-none">
                        <span className="font-bold text-[#db3b6d]">*</span> Tải
                        xuống sau khi đã nộp bài
                      </p>  
                    </>
                  )}

                  <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-end">
                    {/* Navigation Arrows */}
                    <button
                      onClick={handlePrev}
                      className="w-9 h-9 sm:w-10 sm:h-10 bg-[#f6d2df] hover:bg-[#db3b6d] hover:text-white hover:border-[#db3b6d] text-[#4b4b4b] border border-[#4b4b4b] rounded-md flex items-center justify-center transition-colors"
                      aria-label="Trang trước"
                    >
                      <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                      onClick={handleNext}
                      className="w-9 h-9 sm:w-10 sm:h-10 bg-[#f6d2df] hover:bg-[#db3b6d] hover:text-white hover:border-[#db3b6d] text-[#4b4b4b] border border-[#4b4b4b] rounded-md flex items-center justify-center transition-colors"
                      aria-label="Trang sau"
                    >
                      <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Latest Tests */}
            <div className="hidden lg:flex items-start">
              <RightList />
            </div>
          </div>

          {/* Right Sidebar on mobile (below main content) */}
          <div className="mt-6 flex justify-center lg:hidden">
            <RightList />
          </div>
        </section>
      ) : (
        <section className="max-w-[88rem] mx-auto px-4 mt-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-[#4b4b4b] text-lg">Không tìm thấy đề thi</div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ExamInfoPage;
